// mockNoncomplianceData.js
// Location: src/mockData/mockNoncomplianceData.js
//
// Generates deterministic noncompliance data for all 12 modules.
// Identifiers (woNum, itemNum, batchNum) sourced directly from mockModuleData.js.
// Station / Task / Checklist structure sourced from Noncompliance.md.
// To connect live data: replace MOCK_NONCOMPLIANCE_DATA with your API call result.

// ─── Deterministic seeder ─────────────────────────────────────────────────────

function sr(n) {
  const x = Math.sin(n + 1) * 10000;
  return x - Math.floor(x);
}
function pick(arr, n) {
  return arr[Math.floor(sr(n) * arr.length)];
}
function pickDate(n) {
  // Dates fall between 2026-04-01 05:00 and 2026-04-20 14:00 per spec
  const start = new Date("2026-04-01T05:00:00").getTime();
  const end = new Date("2026-04-20T14:00:00").getTime();
  return new Date(start + sr(n) * (end - start)).toISOString();
}
function hm(mins) {
  const h = Math.floor(mins / 60),
    m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// ─── Personnel ────────────────────────────────────────────────────────────────
// Source: Noncompliance.md — strictly observed for updatedBy values.

const WORKERS = [
  "Tim Caine",
  "Rowan Ripley",
  "Armond Littleton",
  "Matt Johnson",
  "Brok Glazewski",
  "Francisco Zainos",
  "Myriah Gass",
  "Michael Taylor",
  "Dominick Teller",
  "Frederick Straun",
  "Robbie Bauder",
  "Jose Casillas",
  "Andrew Volpetti",
  "Joseph Goritski",
  "Christian Garay",
];

// QA/QC tasks ONLY — may not appear on non-QA tasks
const QA_WORKERS = ["Dominic Festa", "Charles Parker"];

// ─── Nonconformance reason pool ───────────────────────────────────────────────

const NC_REASONS = [
  "Dimension out of spec per drawing requirements",
  "Mill Lot # not documented on traveler",
  "Weld does not meet WPS requirements — rework required",
  "Fastener torque spec not met",
  "Photo documentation missing at time of submission",
  "Material does not match cut list specification",
  "Fixture alignment deviation exceeds drawing tolerance",
  "Pipe slope out of tolerance — re-slope required",
  "Insulation gap detected — rework required",
  "Connector not fully seated per manufacturer requirements",
  "Hat channel spacing non-compliant with engineering spec",
  "Seam seal incomplete — reapplication required",
];

// ─── Checklist item type constants ────────────────────────────────────────────

const STATUS = "status"; // Normal pass / fail / NC submission
const IMAGE = "image"; // Image submitted in lieu of completion status
const ESIG = "esignature"; // Electronic signature required

// ─── Station / Task / Checklist definitions ───────────────────────────────────
// Source: Noncompliance.md
// Each checklist item: { name, type: STATUS|IMAGE|ESIG, hasPhoto?: bool }
//   hasPhoto — checklist line includes "(Photo also uploaded here)" in source doc

const STATION_DEFS = [
  // ── Station 1 ──────────────────────────────────────────────────────────────
  {
    stationId: 1,
    stationNumber: 1,
    stationName: "Station 1",
    tasks: [
      {
        taskName: "Steel Cutting",
        isQA: false,
        checklist: [
          { name: "Verify all documentation is available", type: STATUS },
          {
            name: "Cut material as specified for fabrication and document the Mill Lot # and Heat # on the paperwork supplied",
            type: STATUS,
          },
          {
            name: "Ensure that all steel including drop sections are properly identified with Mill Lot# and Heat#",
            type: STATUS,
          },
        ],
      },
    ],
  },

  // ── Station 2 ──────────────────────────────────────────────────────────────
  {
    stationId: 2,
    stationNumber: 2,
    stationName: "Station 2",
    tasks: [
      {
        taskName: "Chassis Roof Fabrication",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials have Mill Lot # and Heat # and it matches the number on the cut list provided with the traveler",
            type: STATUS,
          },
          {
            name: "Verify that the cut list and the traveler are for the same unit",
            type: STATUS,
          },
          {
            name: "Verify vector blocks and steel meet requirements",
            type: STATUS,
          },
          {
            name: "Record vector block Lot # and Heat # on the cut list",
            type: STATUS,
          },
          {
            name: "Place the components into the fixture in the correct location for the size of the unit specified",
            type: STATUS,
          },
          {
            name: "Verify that the length, width and hypotenuses are within drawing requirements",
            type: STATUS,
          },
          {
            name: "Tack Weld all corners in place. Reverify that all dimensions are still within tolerance prior to welding",
            type: STATUS,
          },
          { name: "Weld chassis roof per WPS", type: STATUS },
          {
            name: "Remove clamps from weld fixture. Verify all dimension requirements are still maintained. Welding Lead and Welding Inspector to verify",
            type: STATUS,
          },
        ],
      },
      {
        taskName: "Roof Hat Channel and Drywall Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all hat channel have been properly installed per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Install gypsum ceiling board in areas specified per drawing requirements, install fasteners per specification and building codes",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 3 ──────────────────────────────────────────────────────────────
  {
    stationId: 3,
    stationNumber: 3,
    stationName: "Station 3",
    tasks: [
      {
        taskName: "Chassis Roof Fabrication",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials have Mill Lot # and Heat # and it matches the number on the cut list provided with the traveler",
            type: STATUS,
          },
          {
            name: "Verify that the cut list and the traveler are for the same unit",
            type: STATUS,
          },
          {
            name: "Verify vector blocks and steel meet requirements",
            type: STATUS,
          },
          {
            name: "Record vector block Lot # and Heat # on the cut list",
            type: STATUS,
          },
          {
            name: "Place the components into the fixture in the correct location for the size of the unit specified",
            type: STATUS,
          },
          {
            name: "Verify that the length, width and hypotenuses are within drawing requirements",
            type: STATUS,
          },
          {
            name: "Tack Weld all corners in place. Reverify that all dimensions are still within tolerance prior to welding",
            type: STATUS,
          },
          { name: "Weld chassis roof per WPS", type: STATUS },
          {
            name: "Remove clamps from weld fixture. Verify all dimension requirements are still maintained. Welding Lead and Welding Inspector to verify",
            type: STATUS,
          },
        ],
      },
      {
        taskName: "Chassis Completion",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Place the roof chassis into the fixture in the correct position",
            type: STATUS,
          },
          {
            name: "Verify that the length, width and hypotenuses are within drawing requirements",
            type: STATUS,
          },
          {
            name: "Tack Weld all corners in place. Reverify that all dimensions are still within tolerance prior to welding",
            type: STATUS,
          },
          { name: "Weld chassis components together per WPS", type: STATUS },
          {
            name: "Remove clamps from weld fixture. Verify all dimension requirements are still maintained",
            type: STATUS,
          },
          {
            name: "Stamp unit identification onto the header of the unit",
            type: STATUS,
          },
          {
            name: "Verify all steps are signed off and cut sheet is attached to traveler for record retention. All structural materials must be identified with Mill Lot # and Heat #",
            type: STATUS,
          },
          {
            name: "Welding Lead and Welding Inspector will verify and sign off that all dimensional checks are to drawing requirements (PDF document on file)",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Visual Weld Inspection Report",
        isQA: true,
        checklist: [
          {
            name: "Visually inspect all welds in accordance with applicable drawings",
            type: STATUS,
          },
          {
            name: "Any weld that fails is to be reworked in accordance with WPS and re-inspected",
            type: STATUS,
          },
          {
            name: "Visually inspect any welds that have been reworked in accordance with applicable drawings",
            type: STATUS,
          },
          { name: "Monitored In-process Weld Activity", type: STATUS },
          {
            name: "Take pictures of the welded ceiling for historical purposes",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "All required welds present in accordance with the applicable drawings",
            type: STATUS,
          },
          {
            name: "Visually inspect all welds in accordance with applicable drawings (final)",
            type: STATUS,
          },
          {
            name: "Any weld that fails is to be reworked in accordance with code and re-inspected (final)",
            type: STATUS,
          },
          {
            name: "Visually inspect any welds that have been reworked in accordance with applicable drawings (final)",
            type: STATUS,
          },
          { name: "Monitored In-process Weld Activity (final)", type: STATUS },
          {
            name: "All welds meet WPS Requirements and applicable code",
            type: STATUS,
          },
          {
            name: "Visually inspect that the weld on the vector Block to the columns, Column to Column and stringers sides are ground flush up to +1/8",
            type: STATUS,
          },
          {
            name: "Take pictures of the welded chassis for historical purposes",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Certification of Compliance: All work complies with approved documents, plans, specifications and the uniform building code per AWS D1.1 (2020)",
            type: ESIG,
          },
        ],
      },
    ],
  },

  // ── Station 4 ──────────────────────────────────────────────────────────────
  {
    stationId: 4,
    stationNumber: 4,
    stationName: "Station 4",
    tasks: [
      {
        taskName: "CNC Operations",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify correct material is used per drawing and specification requirements",
            type: STATUS,
          },
          {
            name: "Verify correct program is used to cut unit requirements",
            type: STATUS,
          },
          { name: "Cut subfloor per engineering specifications", type: STATUS },
          {
            name: "Verify cut locations and dimensions are correct",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Sub Floor Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that correct flooring has been provided and the correct number of pieces have been prepared and provided for install",
            type: STATUS,
          },
          {
            name: "Position, square and secure the first piece in place with the proper fasteners per engineering requirements and specifications provided",
            type: STATUS,
          },
          {
            name: "Lay out floor in the locations required and on center and that spacing is per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Verify all flooring has been installed and does not extend over the outside of the frame structure",
            type: STATUS,
          },
          {
            name: "Seal all openings in the floor greater than 1/8 of an inch with the proper sealant per engineering requirements and specifications",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Exterior, Interior Metal Wall and Ceiling Fabrication",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify the cut sheet matches the engineering drawing requirements for the unit — quantities, dimensions and material specifications",
            type: STATUS,
          },
          {
            name: "Prepare the machine with the correct material and program necessary to fabricate",
            type: STATUS,
          },
          {
            name: "Verify that the items produced are identified per engineering requirements",
            type: STATUS,
          },
          {
            name: "Assemble the wall sections as required to engineering drawings and specification with the correct fasteners and stage for next operation",
            type: STATUS,
          },
          {
            name: "Verify overall outside dimensions and all door and window openings",
            type: STATUS,
          },
          {
            name: "Verify insulation has been installed in all box headers and double studs",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Exterior Metal Wall Installation",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify the correct wall sections have been provided and properly identified for traceability and meet drawing requirements",
            type: STATUS,
          },
          {
            name: "Place the wall sections in place per print. All dimensions and locations must be verified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Secure the wall section into place using the correct fasteners and tools specified by the manufacturer and engineering",
            type: STATUS,
          },
          {
            name: "Verify that all wall sections have been properly installed",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 5 ──────────────────────────────────────────────────────────────
  {
    stationId: 5,
    stationNumber: 5,
    stationName: "Station 5",
    tasks: [
      {
        taskName: "Drywall Install Upper Ceiling and Exterior Walls",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install gypsum wall and ceiling board in areas specified per drawing requirements, specification and building codes",
            type: STATUS,
          },
          {
            name: "Verify ceiling and all walls have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Hat Channel Ceiling",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all hat channel have been properly installed per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 6 ──────────────────────────────────────────────────────────────
  {
    stationId: 6,
    stationNumber: 6,
    stationName: "Station 6",
    tasks: [
      {
        taskName: "Mud — First Coat Ext Wall & Upper Ceiling",
        isQA: false,
        checklist: [
          {
            name: "Install first coat mud in areas per specification and building codes",
            type: STATUS,
          },
          {
            name: "Use care not to cover electric, plumbing or HVAC openings that must remain open for access or future connections",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 7 ──────────────────────────────────────────────────────────────
  {
    stationId: 7,
    stationNumber: 7,
    stationName: "Station 7",
    tasks: [
      {
        taskName: "Lower Ceiling Framing Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify the correct wall and ceiling sections have been provided and properly identified for traceability and meet drawing requirements",
            type: STATUS,
          },
          {
            name: "Place the sections in place per print. All dimensions and locations must be verified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Secure the interior wall and lower ceiling sections into place using the correct fasteners and tools specified by the manufacturer and engineering",
            type: STATUS,
          },
          {
            name: "Verify that all sections have been properly installed",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Interior Wall Framing Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify the correct wall and ceiling sections have been provided and properly identified for traceability and meet drawing requirements",
            type: STATUS,
          },
          {
            name: "Place the sections in place per print. All dimensions and locations must be verified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Secure the interior wall and lower ceiling sections into place using the correct fasteners and tools specified by the manufacturer and engineering",
            type: STATUS,
          },
          {
            name: "Verify that all sections have been properly installed",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 8 ──────────────────────────────────────────────────────────────
  {
    stationId: 8,
    stationNumber: 8,
    stationName: "Station 8",
    tasks: [
      {
        taskName: "Fire Sprinkler Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install and ensure that all Fire Sprinkler installations are per engineering, specification, manufacturers specs and building code requirements",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Fire Sprinkler test START — Picture Required",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Sprinkler test END — Picture Required",
            type: STATUS,
            hasPhoto: true,
          },
          { name: "Test paperwork filled out", type: STATUS },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "HVAC Rough-In Installation",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install HVAC rough-in as specified per drawing, specification and code requirements",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Ensure that interior and exterior openings are clearly identified so that they are not covered during other manufacturing operations",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Ensure that all seams are sealed and ducking is supported every 4'",
            type: STATUS,
          },
          {
            name: "Connect condensate pump hose to PVC and insure slope is correct",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Roof Insulation & Decking Install",
        isQA: false,
        checklist: [
          {
            name: "Verify that the correct insulation is provided and installed per specification and engineering requirements",
            type: STATUS,
          },
          {
            name: "Verify that the correct decking is provided and installed per specification and engineering requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 9 ──────────────────────────────────────────────────────────────
  {
    stationId: 9,
    stationNumber: 9,
    stationName: "Station 9",
    tasks: [
      {
        taskName: "EPS & Cover Board Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install EPS & Cover Board per manufacturer and Engineering specification requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Rough Plumbing",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install DWV plumbing per engineering, specification requirements and building codes",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Verify that all plumbing is properly insulated and secured and sloped correctly",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 10 ─────────────────────────────────────────────────────────────
  {
    stationId: 10,
    stationNumber: 10,
    stationName: "Station 10",
    tasks: [
      {
        taskName: "Roofing Install",
        isQA: false,
        checklist: [
          { name: "Roof System Type", type: STATUS, hasPhoto: true },
          { name: "Warranty Length", type: STATUS },
          { name: "Seams Check", type: STATUS },
          { name: "Base Tie-Ins Check", type: STATUS },
          { name: "Terminations Check", type: STATUS },
          { name: "Drains & Scuppers Check", type: STATUS },
          { name: "Misc. Check", type: STATUS },
          { name: "1. Water Block Installed", type: STATUS },
          { name: "2. Upper Clamp Installed", type: STATUS },
          { name: "3. All Bolts are Installed", type: STATUS },
          { name: "4. Horizontal Pipe Supported By Hangers", type: STATUS },
          { name: "5. Elbow Supported By Hangers", type: STATUS },
          {
            name: "Install and ensure that all electrical installations are per engineering, specification and building code requirements",
            type: STATUS,
          },
          {
            name: "Verify all connections have been properly connected and all safety devices and requirements addressed",
            type: STATUS,
          },
        ],
      },
      {
        taskName: "Rough Electric",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install and ensure that all electrical installations are per engineering, specification and building code requirements",
            type: STATUS,
          },
          {
            name: "Verify all connections have been properly connected and all safety devices and requirements addressed",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 11 ─────────────────────────────────────────────────────────────
  {
    stationId: 11,
    stationNumber: 11,
    stationName: "Station 11",
    tasks: [
      {
        taskName: "Rough Electric Panel Make Up",
        isQA: false,
        checklist: [
          {
            name: "Make up all electrical boxes per building codes",
            type: STATUS,
          },
          { name: "Make up electrical panel per building codes", type: STATUS },
          { name: "Fireproof per code requirements", type: STATUS },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Drywall Install Interior Lower Ceiling",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install gypsum wall and ceiling board in areas specified per drawing requirements, specification and building codes",
            type: STATUS,
          },
          {
            name: "Verify all ceilings have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Install gypsum wall board in areas specified per drawing requirements, specification and building codes",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 12 ─────────────────────────────────────────────────────────────
  {
    stationId: 12,
    stationNumber: 12,
    stationName: "Station 12",
    tasks: [
      {
        taskName: "Insulation Install",
        isQA: false,
        checklist: [
          {
            name: "Verify that the correct insulation is provided and installed per specification and engineering requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Drywall Install Interior Walls",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install gypsum wall and ceiling board in areas specified per drawing requirements, specification and building codes",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 13 ─────────────────────────────────────────────────────────────
  {
    stationId: 13,
    stationNumber: 13,
    stationName: "Station 13",
    tasks: [
      {
        taskName: "Mud — First Coat",
        isQA: false,
        checklist: [
          {
            name: "Install first coat mud in areas per specification and building codes",
            type: STATUS,
          },
          {
            name: "Use care not to cover electric, plumbing or HVAC openings that must remain open for access or future connections",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 14 ─────────────────────────────────────────────────────────────
  {
    stationId: 14,
    stationNumber: 14,
    stationName: "Station 14",
    tasks: [
      {
        taskName: "Mud — Second Coat",
        isQA: false,
        checklist: [
          {
            name: "Install second coat mud in areas per specification and building codes",
            type: STATUS,
          },
          {
            name: "Use care not to cover electric, plumbing or HVAC openings that must remain open for access or future connections",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Exterior Sheathing Installation",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Fasten sheathing per manufacturer's installation instructions to meet and comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Install Texture coat in areas per specification",
            type: STATUS,
          },
          {
            name: "Use care not to cover electric, plumbing or HVAC openings that must remain open for access or future connections",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
          },
          {
            name: "Cover, tape and protect necessary areas prior to starting operation",
            type: STATUS,
          },
          {
            name: "Finish all areas on inside of unit as specified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Ensure necessary areas are protected prior to starting operation",
            type: STATUS,
          },
          {
            name: "Install flooring to manufacturer's installation instructions and specification requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 15 ─────────────────────────────────────────────────────────────
  {
    stationId: 15,
    stationNumber: 15,
    stationName: "Station 15",
    tasks: [
      {
        taskName: "Mud — Texture Coat",
        isQA: false,
        checklist: [
          {
            name: "Install Texture coat in areas per specification",
            type: STATUS,
          },
          {
            name: "Use care not to cover electric, plumbing or HVAC openings that must remain open for access or future connections",
            type: STATUS,
          },
          {
            name: "Verify all walls have been covered to requirements",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 16 ─────────────────────────────────────────────────────────────
  {
    stationId: 16,
    stationNumber: 16,
    stationName: "Station 16",
    tasks: [
      {
        taskName: "Interior Prime Paint Coat",
        isQA: false,
        checklist: [
          {
            name: "Cover, tape and protect necessary areas prior to starting operation",
            type: STATUS,
          },
          {
            name: "Finish all areas on inside of unit as specified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Ensure necessary areas are protected prior to starting operation",
            type: STATUS,
          },
          {
            name: "Finish all areas on inside of unit as specified per engineering documentation (prime coat)",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Interior Paint Finish",
        isQA: false,
        checklist: [
          {
            name: "Ensure necessary areas are protected prior to starting operation",
            type: STATUS,
          },
          {
            name: "Finish all areas on inside of unit as specified per engineering documentation",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 17 ─────────────────────────────────────────────────────────────
  {
    stationId: 17,
    stationNumber: 17,
    stationName: "Station 17",
    tasks: [
      {
        taskName: "Drying Station",
        isQA: false,
        checklist: [
          { name: "This Area Conforms", type: STATUS, hasPhoto: true },
        ],
      },
    ],
  },

  // ── Station 18 ─────────────────────────────────────────────────────────────
  {
    stationId: 18,
    stationNumber: 18,
    stationName: "Station 18",
    tasks: [
      {
        taskName: "Finish Flooring",
        isQA: false,
        checklist: [
          {
            name: "Install flooring to manufacturer's installation instructions and specification requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Exterior Sheathing Waterproofing Tape & Sealing",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Tape, seal and finish sheathing per manufacturer's installation instructions to meet and comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Seal all nail holes and any penetrations with sealant per manufacturer's specification requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 19 ─────────────────────────────────────────────────────────────
  {
    stationId: 19,
    stationNumber: 19,
    stationName: "Station 19",
    tasks: [
      {
        taskName: "Cabinet Install",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Countertops",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Install per general shop practice to meet and comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 20 ─────────────────────────────────────────────────────────────
  {
    stationId: 20,
    stationNumber: 20,
    stationName: "Station 20",
    tasks: [
      {
        taskName: "Plumbing Fixture Installation",
        isQA: false,
        checklist: [
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Plumbing Test",
        isQA: false,
        checklist: [
          { name: "Perform water test per approved procedure", type: STATUS },
          { name: "Water test start", type: STATUS, hasPhoto: true },
          { name: "Water test end", type: STATUS, hasPhoto: true },
          { name: "Perform DWV test per approved procedure", type: STATUS },
          { name: "DWV test start", type: STATUS, hasPhoto: true },
          { name: "DWV test end", type: STATUS, hasPhoto: true },
        ],
      },
      {
        taskName: "Electric Trim & Fixture Installation",
        isQA: false,
        checklist: [
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "HVAC Trim Install",
        isQA: false,
        checklist: [
          {
            name: "Verify that all materials were supplied to complete this operation per engineering and specification requirements",
            type: STATUS,
          },
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Verify all HVAC hardware is in mod for ship loose",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 20 — QC ────────────────────────────────────────────────────────
  {
    stationId: 21,
    stationNumber: 20.5,
    stationName: "Station 20 — QC",
    tasks: [
      {
        taskName: "SERACLAD Facade Siding",
        isQA: true,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Install all trim per drawings and make sure all trim match color layout per drawings",
            type: STATUS,
          },
          {
            name: "Install all fasteners per manufacturers installation instructions to comply to engineering, specifications",
            type: STATUS,
          },
          {
            name: "Install all panels per drawings and make sure all panels match color layout per drawings",
            type: STATUS,
          },
          {
            name: "Install all face fasteners per drawings and fill and paint all face fasteners",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist — PHOTO REQUIRED",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 21 ─────────────────────────────────────────────────────────────
  {
    stationId: 22,
    stationNumber: 21,
    stationName: "Station 21",
    tasks: [
      {
        taskName: "Appliance Installed",
        isQA: false,
        checklist: [
          {
            name: "Install all appliances and furniture and secure for shipment",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Interior/Exterior Door Installed",
        isQA: false,
        checklist: [
          { name: "Built To Shop Drawing#", type: IMAGE },
          { name: "Rev", type: IMAGE },
          {
            name: "Verify the correct doors are available per drawing requirements including the correct hand per print",
            type: STATUS,
          },
          {
            name: "All dimensions and locations must be verified per engineering documentation",
            type: STATUS,
          },
          { name: "Verify exterior door labels are attached", type: STATUS },
          {
            name: "Secure the windows and doors using the correct fasteners and tools specified by manufacturer and engineering, specifications and codes",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Electrical Exterior Trim Install",
        isQA: false,
        checklist: [
          {
            name: "Install per manufacturers installation instructions to comply to engineering, specifications, and code requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Ship Loose Items",
        isQA: false,
        checklist: [
          {
            name: "Verify all bath hardware is in mod for ship loose",
            type: STATUS,
          },
          {
            name: "Verify all door trim & base trim is in mod for ship loose",
            type: STATUS,
          },
          {
            name: "Verify all ship loose items are in mod and ship loose paper list in mod both sides",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 22 ─────────────────────────────────────────────────────────────
  {
    stationId: 23,
    stationNumber: 22,
    stationName: "Station 22",
    tasks: [
      {
        taskName: "Electrical Test",
        isQA: true,
        checklist: [
          { name: "Dielectric Strength Test", type: STATUS },
          { name: "Continuity Test", type: STATUS },
          { name: "Electrical Fixture Operational Test", type: STATUS },
          { name: "Smoke Alarm Operation Test", type: STATUS },
          {
            name: "Verify all test procedures have been validated and met minimum requirements",
            type: STATUS,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
      {
        taskName: "Final Inspection",
        isQA: true,
        checklist: [
          {
            name: "Review all inspection checklists have been completed and discrepancies resolved and signed off",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Validate all test procedures have been completed and any issues noted were addressed and signed off",
            type: STATUS,
          },
          {
            name: "Verify that all material certifications are noted in the documentation package",
            type: STATUS,
          },
          {
            name: "Inspect that all items have been secured for shipment",
            type: STATUS,
          },
          {
            name: "Attach Data Plate and Decals or Insignia as required per contract",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Sign off documentation for unit release and shipment",
            type: STATUS,
          },
          { name: "Secure all documentation for unit", type: STATUS },
          {
            name: "Submit documentation for record",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },

  // ── Station 23 ─────────────────────────────────────────────────────────────
  {
    stationId: 24,
    stationNumber: 23,
    stationName: "Station 23",
    tasks: [
      {
        taskName: "Plastic Wrap",
        isQA: false,
        checklist: [
          {
            name: "Install support plates and alignment pins as required",
            type: STATUS,
          },
          {
            name: "Protect corners and sharp edges that may damage protective liner",
            type: STATUS,
          },
          {
            name: "Wrap unit in plastic and heat to secure in place during transport to site",
            type: STATUS,
          },
          {
            name: "Ensure that unit is identified per engineering, documentation and contract requirements",
            type: STATUS,
            hasPhoto: true,
          },
          {
            name: "Take a Photo of the Module before submitting the checklist",
            type: STATUS,
            hasPhoto: true,
          },
        ],
      },
    ],
  },
];

// ─── Module identifiers — sourced directly from mockModuleData.js ─────────────

const MODULE_IDS = [
  {
    woNum: "12WO001001",
    itemNum: "P42-PB2-FR-107",
    batchNum: "CDLRZ00205103&04002",
  },
  {
    woNum: "12WO001003",
    itemNum: "P42-PB2-FR-207",
    batchNum: "CDLRZ00205203&04002",
  },
  {
    woNum: "12WO001005",
    itemNum: "P42-PB2-FRC-307",
    batchNum: "CDLRZ00205303&04002",
  },
  {
    woNum: "12WO001002",
    itemNum: "P42-PB1-MR-108",
    batchNum: "CDLRZ32603117E002",
  },
  {
    woNum: "12WO001012",
    itemNum: "P42-PL2-TC-314",
    batchNum: "CDLRZ00205307002",
  },
  {
    woNum: "12WO001013",
    itemNum: "P42-PB4-WA-101",
    batchNum: "CDLRZ00205113&14002",
  },
  {
    woNum: "12WO001006",
    itemNum: "P42-PB1-MR-208",
    batchNum: "CDLRZ00205103&04006",
  },
  {
    woNum: "12WO001007",
    itemNum: "P42-PB1-MRC-308",
    batchNum: "CDLRZ00205203&04006",
  },
  {
    woNum: "12WO001008",
    itemNum: "P42-PL1-TG.2-109",
    batchNum: "CDLRZ00205303&04006",
  },
  {
    woNum: "12WO001009",
    itemNum: "P42-PL1-TG.2-309",
    batchNum: "CDLRZ32603217E002",
  },
  {
    woNum: "12WO001010",
    itemNum: "P42-PL2-T-114",
    batchNum: "CDLRZ00205107002",
  },
  {
    woNum: "12WO001011",
    itemNum: "P42-PL2-T-214",
    batchNum: "CDLRZ00205207002",
  },
];

// ─── Data builders ────────────────────────────────────────────────────────────

function buildVersionHistory(seed, isCorrected) {
  if (!isCorrected) return [];
  const count = 1 + Math.floor(sr(seed * 7) * 2);
  return Array.from({ length: count }, (_, v) => ({
    versionId: `v-${seed}-${v}`,
    submissionValue: v === 0 ? "Non Conformed" : "Corrected",
    submittedAt: pickDate(seed * 13 + v * 500),
    submittedBy: pick(WORKERS, seed + v * 3),
    notes: pick(NC_REASONS, seed + v),
  }));
}

function buildChecklistItem(item, taskIsQA, modIdx, stIdx, tIdx, cIdx) {
  // Seed composed of all hierarchy levels to guarantee uniqueness
  const seed = modIdx * 1000000 + stIdx * 100000 + tIdx * 10000 + cIdx * 100;
  const workers = taskIsQA ? QA_WORKERS : WORKERS;

  // ── Image-upload item ──────────────────────────────────────────────────────
  if (item.type === IMAGE) {
    return {
      id: `ci-${seed}`,
      name: item.name,
      submissionType: "image",
      submissionValue: "Image Submitted",
      updatedAt: pickDate(seed + 1),
      updatedBy: pick(workers, seed + 2),
      stepComment: "",
      images: [`https://picsum.photos/seed/${seed}/400/300`],
      versionHistory: [],
    };
  }

  // ── E-signature item ───────────────────────────────────────────────────────
  if (item.type === ESIG) {
    const signed = sr(seed * 5) > 0.25;
    return {
      id: `ci-${seed}`,
      name: item.name,
      submissionType: "esignature",
      submissionValue: signed ? "Signed" : "Pending Signature",
      updatedAt: pickDate(seed + 1),
      updatedBy: signed ? pick(workers, seed + 2) : null,
      stepComment: "",
      images: [],
      versionHistory: [],
    };
  }

  // ── Normal status item ─────────────────────────────────────────────────────
  const r = sr(seed * 3);
  const sv =
    r < 0.62
      ? "Completed"
      : r < 0.8
        ? "Non Conformed"
        : r < 0.92
          ? "Corrected"
          : "N/A";
  const isNC = sv === "Non Conformed" || sv === "Corrected";
  const imgCount = item.hasPhoto
    ? sv === "Completed"
      ? 1
      : Math.floor(sr(seed + 7) * 2) + 1
    : 0;

  return {
    id: `ci-${seed}`,
    name: item.name,
    submissionType: "status",
    submissionValue: sv,
    updatedAt: pickDate(seed + 1),
    updatedBy: pick(workers, seed + 2),
    stepComment: isNC ? pick(NC_REASONS, seed + 3) : "",
    images: Array.from(
      { length: imgCount },
      (_, ii) => `https://picsum.photos/seed/${seed + ii * 11}/400/300`,
    ),
    versionHistory: buildVersionHistory(seed, sv === "Corrected"),
  };
}

function buildTask(taskDef, modIdx, stIdx, tIdx) {
  return {
    taskId: `task-${modIdx}-${stIdx}-${tIdx}`,
    taskName: taskDef.taskName,
    taskDescription: "",
    isQA: taskDef.isQA || false,
    subtasks: taskDef.checklist.map((item, cIdx) =>
      buildChecklistItem(
        item,
        taskDef.isQA || false,
        modIdx,
        stIdx,
        tIdx,
        cIdx,
      ),
    ),
  };
}

function buildStation(stDef, modIdx, stIdx) {
  const seed = modIdx * 10000 + stIdx * 100;
  const timeMinutes = 30 + Math.floor(sr(seed + 50) * 210);
  return {
    stationId: stDef.stationId,
    stationNumber: stDef.stationNumber,
    stationName: stDef.stationName,
    timeInStation: hm(timeMinutes),
    tasks: stDef.tasks.map((taskDef, tIdx) =>
      buildTask(taskDef, modIdx, stIdx, tIdx),
    ),
  };
}

// ─── Final export ─────────────────────────────────────────────────────────────

export const MOCK_NONCOMPLIANCE_DATA = MODULE_IDS.map((mod, modIdx) => ({
  manufacturedItemId: `item-${mod.woNum}`,
  woNum: mod.woNum,
  itemNum: mod.itemNum,
  batchNum: mod.batchNum,
  createdAt: pickDate(modIdx * 13 + 1000),
  lastSeenAt: pickDate(modIdx * 7 + 2000),
  stations: STATION_DEFS.map((stDef, stIdx) =>
    buildStation(stDef, modIdx, stIdx),
  ),
})).sort((a, b) => new Date(b.lastSeenAt) - new Date(a.lastSeenAt));

export default MOCK_NONCOMPLIANCE_DATA;
